import { ActionReducerMapBuilder, createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit";
import { RootState } from "../../../../../app/store";

import {
    fetchSCNResponseReportsLegacyThunk,
    fetchSCNResponseReportsThunk,
    putSCNResponseLegacyThunk,
    putSCNResponseThunk,
} from "./scnResponseReportsThunk";

import { SCNResponseReport } from "../../../../../api/scnResponseReports";
import { addCaseDocument } from "../../../../../thunks/caseDocument";
import { DocumentType } from "../../../../../api/caseDocuments";

export interface SCNResponseReportsState {
    scn_responses: EntityState<SCNResponseReport, string>;
    status: "idle" | "loading" | "succeeded" | "saving" | "failed";
    error: string | null;
}

const scnResponseAdapter = createEntityAdapter<SCNResponseReport, string>({
    selectId: (report) => report.party_id,
});

const initialState: SCNResponseReportsState = {
    scn_responses: scnResponseAdapter.getInitialState(),
    status: "idle",
    error: null,
};

const addFetchCases = (
    builder: ActionReducerMapBuilder<SCNResponseReportsState>,
    thunk: typeof fetchSCNResponseReportsLegacyThunk
) => {
    builder
        .addCase(thunk.pending, (state) => {
            state.status = "loading";
            state.error = null;
            scnResponseAdapter.removeAll(state.scn_responses)
        })
        .addCase(thunk.rejected, (state, action) => {
            state.status = "failed";
            state.error = action.payload || "Failed to fetch SCN Response Reports";
        })
        .addCase(thunk.fulfilled, (state, action) => {
            state.status = "succeeded";
            state.error = null;
            scnResponseAdapter.setAll(state.scn_responses, action.payload.responses ?? []);
        });
};

const addPutCases = (
    builder: ActionReducerMapBuilder<SCNResponseReportsState>,
    thunk: typeof putSCNResponseLegacyThunk | typeof putSCNResponseThunk
) => {
    builder
        .addCase(thunk.pending, (state) => {
            state.status = "saving";
            state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
            state.status = "failed";
            state.error = action.payload || "Failed to save SCN Response";
        })
        .addCase(thunk.fulfilled, (state, action: any) => {
            const args = action.meta.arg;
            const party_id = args.party_id ?? args.request?.party_id;
            const report_id = args.report_id;
            const is_response_received = args.is_response_received ?? args.request?.is_response_received;
            const file_name = args.file?.name;

            scnResponseAdapter.upsertOne(state.scn_responses, {
                party_id,
                report_id: report_id ?? party_id,
                is_response_received,
                file_name,
                last_updated_at: new Date().toISOString(),
            });

            state.status = "succeeded";
            state.error = null;
        })
};

const scnResponseSlice = createSlice({
    name: "scnResponse",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Legacy
        addFetchCases(builder, fetchSCNResponseReportsLegacyThunk);
        addPutCases(builder, putSCNResponseLegacyThunk);

        // V2
        addFetchCases(builder, fetchSCNResponseReportsThunk);
        addPutCases(builder, putSCNResponseThunk);
        
        builder.addCase(addCaseDocument.fulfilled, (state, action) => {
            const { payload } = action.meta.arg;

            if (payload.document_type !== DocumentType.SCN_RESPONSE) {
                return;
            }

            const partyId = payload.entity_id;

            scnResponseAdapter.upsertOne(state.scn_responses, {
                party_id: partyId,
                report_id: partyId,
                is_response_received: true,
                file_name: payload.file_name,
                last_updated_at: new Date().toISOString(),
            });
       });
    },
});

export const scnResponseReportsReducer = scnResponseSlice.reducer;

const selectSCNResponseState = (state: RootState): SCNResponseReportsState =>
    state.scnResponseReports;

const scnResponseSelectors = scnResponseAdapter.getSelectors<RootState>(
    (state) => selectSCNResponseState(state).scn_responses,
);

export const { selectEntities: selectSCNResponseEntities } = scnResponseSelectors;

export const selectSCNResponseStatus = (state: RootState) =>
    selectSCNResponseState(state).status;

export const selectSCNResponseError = (state: RootState) =>
    selectSCNResponseState(state).error;

export const selectSCNResponseByPartyId = (state: RootState, partyId: string): SCNResponseReport | undefined =>
    selectSCNResponseEntities(state)[partyId];

export const selectSCNResponseDefaultSelectionByPartyId = (
    state: RootState,
    partyId: string
): "not-received" | "" => {
    const response = selectSCNResponseEntities(state)[partyId];

    if (response && response.is_response_received === false) {
        return "not-received";
    }

    return "";
};